import { Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import { AngleDownIcon, AngleUpIcon, LoadingIcon } from 'components/Icons';
import { SEVERITY_COLOR } from 'constants/Layout';
import { PermissionSetMap } from 'constants/RBACConstants';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import useActiveSeveritiesKeys from 'hooks/useActiveSeveritiesKeys';
import _, { find, isEmpty, isString, noop, omitBy, pick } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import FindingAPI from 'restAPI/FindingAPI';
import { CellRenderers, ColumnFilterTypes, getFilterProps, Sorters } from 'utils/tableUtils';
import { FINDING_STATUS, BULK_FINDING_ACTION_KEYS, TWISTLOCK_SOURCE, EXPLOIT_MATURITY_FILTERS, SLA_STATUS_FILTERS, FINDING_STATUS_VOCABULARY_MAP, IPAdressFilterMessage } from './Constants';
import { RiskRatings } from "utils/riskScoreUtil";
import ProductSubProductCount from './ProductSubProductCount';
import { hasPermission } from 'utils/authService';
import { convertAggField, CurrentUserFilterOption, getFilterValues } from './utils';
import useUrlFilters from 'hooks/useUrlFilters';
import { useGlobalSettingsContext, useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';
import useTrigerrableEffect from 'react-triggerable-effect';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import ACColumnFilterKeyValue from 'ac-components/ac-column-filter-key-value';
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import { useGlobalFilterContext } from 'contexts/GlobalFilterContext';
import { PROPOSE_FILTER_OPTION_VALUES, PROPOSED_TYPE } from './FindingsReview';
import ColumnFilterSearchBoxWithExclude from 'components/ColumnFilterSearchBoxWithExclude';
import { TIER_OPTIONS } from 'utils/tierUtil';
import ColumnFilterGroups from 'components/ColumnFilterGroups';
import { EPSSReceiveInterceptor } from './EPSSScore';
import ACDateRangeColumnFilter from 'ac-components/ACDateRangeColumnFilter';
import { parseDateToDisplay } from 'utils/dateUtils';
import ColumnFilterComparison from 'components/ColumnFilterComparison';
import ACColumnFilterGroup from 'ac-components/ac-column-filter-group/ACColumnFilterGroup';
import { formatNumber as formatNumberNew } from 'utils/numberUtil';
import ExceptionAPI from 'restAPI/ExceptionAPI';
import ACCustomAttributeFilterDropdown from 'ac-components/ac-custom-attribute-filter-dropdown/ACCustomAttributeFilterDropdown';
import ACContextualFilterDropdown from 'ac-components/ac-contextual-filter-dropdown/ACContextualFilterDropdown';
import { CustomColumnScope } from 'types/tenantConfig';
import useProductFilterHelper from 'hooks/useProductFilterHelper';
import useSubProductFilterHelper from 'hooks/useSubProductFilterHelper';
import { getEmptyCancellableRequest } from 'restAPI';
import useDetectFilterChange from 'hooks/useDetectFilterChange';
import {
  EXPLOIT_STATUS_OPTIONS,
  NOTIFICATION_STATUS_OPTIONS,
  USER_DISCLOSURE_STATUS_OPTIONS,
} from "utils/craComplianceUtils";
const { Text } = Typography;

const RISK_RATING_FILTERS = Object.values(RiskRatings).map(key => ({ name: key, id: key }));

export const useFindingFilters = ({ businessUnits, externalColumn, fields = undefined, proposeActionNullFilter = true, filters, findingCustomFields } = {}) => {
    const [filterValues, setFilterValues] = useState({
        proposeAction: [
            FINDING_STATUS.FALSE_POSITIVE,
            FINDING_STATUS.ACCEPT_RISK,
            FINDING_STATUS.MITIGATED,
            FINDING_STATUS.SUPPRESSED,
            FINDING_STATUS.TRIAGE,
            FINDING_STATUS.IN_PROGRESS,
            FINDING_STATUS.CONTROLLED,    
            PROPOSED_TYPE.PROPOSE_ASSIGNEE.type,
            PROPOSED_TYPE.PROPOSE_DUE_DATE.type,
            PROPOSED_TYPE.PROPOSE_CATEGORY.type,
            PROPOSED_TYPE.PROPOSE_SEVERITY.type,
            PROPOSED_TYPE.PROPOSE_BASIC_CHANGE.type,
          ]
    });
    const featureFlags = useFeatureFlag();
    const globalFilter = useGlobalFilterContext();
    const {hasDisabledEnvironment} = useHasDisabledEnvironment();
    const globalSettings = useGlobalSettingsContext();
    const { id: activeUserId } = useActiveUserContext();

    const productFilterHelper = useProductFilterHelper({filters});
    const subProductFilterHelper = useSubProductFilterHelper({filters});

    const refreshFilters = useTrigerrableEffect(() => {
        const { cancel, promise } = getFilterValues(
            { businessUnits, fields },
        );
        promise.then((filters) => {
            setFilterValues((prev) => ({ ...prev, ...filters }));
        });
        return cancel;
    }, [businessUnits, globalFilter.globalFilterHash, fields]);

    useEffect(()=> {
        if(featureFlags.EXCEPTION_MODULE && hasPermission(PermissionSetMap.READ_RISK_REGISTER)){
            const {promise, cancel} = ExceptionAPI.getFilterExceptions();
            promise.then((exceptionFilters)=> {
                const capitalizedStatus = (exceptionFilters.status || []).map(s => _.capitalize(s));
                setFilterValues((prev)=> ({...prev, exceptionReason: exceptionFilters.reasons, exceptionStatus: capitalizedStatus, exceptionsReason: exceptionFilters.reasons, exceptionsStatus: capitalizedStatus}))
            })
            return cancel;
        }
    }, [featureFlags.EXCEPTION_MODULE])

    useEffect(() => {
        const {promise: acceptRiskPromise, cancel: cancelAcceptRisk } = GlobalSettingAPI.getAcceptRiskReason();
        const {promise: controlledReasonPromise, cancel: cancelControlledReason } = GlobalSettingAPI.getControlledReason();
    
        Promise.all([acceptRiskPromise, controlledReasonPromise])
          .then(([acceptRiskData, controlledReasonData]) => {
            const allReasons = [
                ...acceptRiskData.reasons, 
                ...controlledReasonData.reasons
              ];
            const uniqueReasons = Array.from(new Set(allReasons));
            setFilterValues((prev) => ({
                ...prev,
                statusChangeReason: uniqueReasons,
            }));
        });
        return () => {
          cancelAcceptRisk();
          cancelControlledReason();
        };
    }, []);

    useEffect(() => {
        const tierOptions = featureFlags.RISK_TIERS
            ? globalSettings.tierBasedTags.map(tier => ({ label: tier.name, value: tier.name }))
            : TIER_OPTIONS;
        setFilterValues((prev) => ({
            ...prev,
            productTier: tierOptions,
            subProductTier: tierOptions,
        }));
    }, [featureFlags.RISK_TIERS, globalSettings.tierBasedTags]);

    const activeSeverities = useActiveSeveritiesKeys(undefined, true);

    const fetchEntityFilterValues = useCallback(({name, filters}) => {
        const isCountNotSupported = ["productStatus", "subProductStatuses"].includes(name);
        const request = ["severity", "toolSeverity", "status"].includes(name) ? {
            promise: Promise.resolve({[name]: name === "status" ? Object.keys(FINDING_STATUS_VOCABULARY_MAP) : activeSeverities}),
            cancel: noop
        } : FindingAPI.getFilterValues({
          fields: [name],
        //   filters,
        });
        const countStatsRequest = isCountNotSupported ? getEmptyCancellableRequest([]) : FindingAPI.getStatsCount({
          aggField: convertAggField(name),
          filters: omitBy(filters, isEmpty),
        });
        const promise = Promise.all([request.promise, countStatsRequest.promise]).then(
          ([response, countResponse]) => {
            const values = (response[name] || []).map(value => isString(value) ? ({name: value, id: value}) : value);
            if(name === "owner"/*  && find(values, {id: activeUserId}) */){
                const countObject = find(countResponse, {id: `${activeUserId}`});
                countResponse.unshift({...countObject, id: CurrentUserFilterOption.id});
                values.unshift({...CurrentUserFilterOption});
            }
            return {
                options: values,
                counts: countResponse
            };
          },
        );
        return {
            promise,
            cancel: () => {
                request.cancel();
                countStatsRequest.cancel();
            }
        };
    }, [activeUserId, activeSeverities]);

    const filterColumns = useMemo(() => {
        return getFindingFilterColumns({
            filterValues,
            hasDisabledEnvironment,
            featureFlags,
            proposeActionNullFilter,
            externalColumn,
            findingCustomFields,
            fetchEntityFilterValues,
            productFilterHelper,
            subProductFilterHelper,
            globalSettings,
        })
    }, [
        filterValues,
        hasDisabledEnvironment,
        featureFlags,
        proposeActionNullFilter,
        externalColumn,
        findingCustomFields,
        fetchEntityFilterValues,
        productFilterHelper,
        subProductFilterHelper,
        globalSettings,
    ]);

    return {
        filterColumns,
        filterValues,
        refreshFilters,
        productList: productFilterHelper.allOptions,
        subProductList: subProductFilterHelper.allOptions,
    }
}

export function getFindingFilterColumns({
    filterValues = {},
    hasDisabledEnvironment,
    featureFlags,
    proposeActionNullFilter,
    externalColumn,
    findingCustomFields,
    fetchEntityFilterValues,
    productFilterHelper = {},
    subProductFilterHelper = {},
    globalSettings = {},
  } = {}){
    const scoreFieldAllLableFilterFormatter = (values) => ColumnFilterComparison.formatter(values, 'Score');
    const countFieldAllLableFilterFormatter = (values) => ColumnFilterComparison.formatter(values, 'Count');
    const daysFieldAllLableFilterFormatter = (values) => ColumnFilterComparison.formatter(values, 'Days');

    const {allOptions: allProducts, renderFilterDropdown: renderProductFilterDropdown} = productFilterHelper;
    const {allOptions: allSubProducts, renderFilterDropdown: renderSubProductFilterDropdown} = subProductFilterHelper;

    const columns = [{
        title: "ID",
        dataIndex: "id",
        // id filter is search on UI, but its not text search. User has to enter full id to get result
        // hence filter operations are not applied
        ...getFilterProps({ onlySearch: true, isMultipleValueSupported: true })
    }, {
        title: "Version ID",
        dataIndex: "versionId",
        // same reason as finding id
        ...getFilterProps({ onlySearch: true })
    }, {
        title: "Description",
        dataIndex: "description",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: getVocabulary("impact"),
        dataIndex: "impact",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    },
    {
        title: getVocabulary("mitigation"),
        dataIndex: "mitigation",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Source Tool",
        dataIndex: "source",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='source' />
        ),
    }, {
        title: "Scan Type",
        dataIndex: "scanType",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown
                {...props}
                componentProps={{ nullFilterValue:null }} 
                name='scanType'
            />
        ),
    }, {
        title: "Summary",
        dataIndex: "title",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    },{
        title: "Scan Identifier",
        dataIndex: "scanIdentifier",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Tool Severity",
        dataIndex: "toolSeverity",
        filterDropdown: (props) => (
            // TODO show all severities
            <ACContextualFilterDropdown {...props} name='toolSeverity' sortOptions={Sorters.SEVERITY("name", true)} />
        ),
    }, {
        title: "Status",
        dataIndex: "status",
        filterIndex: 2,
        filterLabelFormatter: (status) => getVocabulary(FINDING_STATUS_VOCABULARY_MAP[status]),
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='status' sortOptions={Sorters.STATUS("name", true)} />
        ),
    }, {
        title: "Tool Finding Status",
        dataIndex: "toolFindingStatus",
        ...getFilterProps({ onlySearch: true, isMultipleValueSupported: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Resurface Date",
        dataIndex: "unsupressTime",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: `Tool Finding ${getVocabulary("category")}`,
        dataIndex: "findingCategory",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='findingCategory' />
        ),
    }, {
        title: `${getVocabulary("category")}`,
        dataIndex: "armorcodeCategory",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='armorcodeCategory' />
        ),
    }, {
        title: `${getVocabulary("product")}`,
        dataIndex: "product",
        filterIndex: 3,
        filters: allProducts,
        filterDropdown: renderProductFilterDropdown
    }, {
        title: `${getVocabulary("product")} Status`,
        dataIndex: "productStatus",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='productStatus' api={fetchEntityFilterValues} skipSearchAPICall componentProps={{nullFilterValue: null}} showCount={false} />
        ),
    }, {
        title: `${getVocabulary("subProduct")}`,
        dataIndex: "subProduct",
        filters: allSubProducts,
        filterDropdown: renderSubProductFilterDropdown
    }, {
        title: `${getVocabulary("subProduct")} Status`,
        dataIndex: "subProductStatuses",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='subProductStatuses' api={fetchEntityFilterValues} skipSearchAPICall componentProps={{nullFilterValue: null}} showCount={false} />
        ),
    }, ...(hasDisabledEnvironment ? [] : [{
        title: "Environment",
        dataIndex: "environmentName",
        filterLabelFormatter: CellRenderers.VOCABULARY_RENDERER,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='environmentName' />
        ),
    }]), {
        title: `${getVocabulary("project")}`,
        dataIndex: "armorcodeProjects",
        filters: filterValues.armorcodeProjects,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown 
                {...props} 
                skipSearchAPICall 
                name='armorcodeProjects' 
                componentProps={{ nullFilterValue: null }}
            />
        ),
    },  ...(featureFlags.EXCEPTION_MODULE && hasPermission(PermissionSetMap.READ_RISK_REGISTER) ? [{
        title: `${getVocabulary("exception")} Name`,
        dataIndex: "riskRegister",
        filters: filterValues.riskRegister,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown 
                {...props} 
                skipSearchAPICall 
                name='riskRegister' 
                componentProps={{ nullFilterValue: null }}
            />
        ),
    },
    {
        title: `${getVocabulary("exception")} Start Date`,
        dataIndex: "exceptionsStartDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    },
    {
        title: `${getVocabulary("exception")} End Date`,
        dataIndex: "exceptionsEndDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    },
    {
        title: `${getVocabulary("exception")} Reason`,
        dataIndex: "exceptionsReason",
        ...getFilterProps({ filters: filterValues.exceptionReason, nullFilterValue: null})
    },
    {
        title: `${getVocabulary("exception")} Status`,
        dataIndex: "exceptionsStatus",
        ...getFilterProps({ filters: filterValues.exceptionStatus})
    }
    ]: []),
    ...(featureFlags.PENTEST_MODULE
        ? [{
        title: `${getVocabulary("assessment")}`,
        dataIndex: "assessments",
        filters: filterValues.assessments,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown 
                {...props} 
                skipSearchAPICall 
                name='assessments' 
                componentProps={{ nullFilterValue: null }}
            />
        ),
    }] : []), {
        title: `${getVocabulary("team")}`,
        dataIndex: "team",
        filters: filterValues.team,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown 
                {...props} 
                skipSearchAPICall 
                name='team' 
                componentProps={{ nullFilterValue: null }}
            />
        ),
    }, {
        title: "Tags",
        dataIndex: "keyValue",
        filterDropdown: (props) => <ACColumnFilterKeyValue {...props} />,
        filterLabelFormatter: ACColumnFilterKeyValue.filterLabelFormatter,
    }, {
        title: "Ticket ID",
        dataIndex: "armorcodeTicket",
        ...getFilterProps({ 
            onlySearch: true, 
            nullFilterValue: null, 
            isMultipleValueSupported: true, 
            filterLabelFormatter: CellRenderers.VALUE_OR_NA,
            supportFilterOperation: featureFlags.NEW_TABLE_FILTER,
        })
    }, {
        title: "Ticket Status",
        dataIndex: "armorcodeTicketStatus",
        ...getFilterProps({ filters: filterValues.armorcodeTicketStatus, nullFilterValue: null })
    }, {
        title: "Tool Finding ID",
        dataIndex: "toolId",
        ...getFilterProps({ onlySearch: true, nullFilterValue: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: `${getVocabulary("severity")}`,
        dataIndex: "severity",
        filterIndex: 1,
        filterLabelFormatter: (name) => getVocabulary(name),
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='severity' sortOptions={Sorters.SEVERITY("name", true)} />
        ),
    }, {
        title: "AMC Rule",
        dataIndex: "ruleId",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Repository",
        dataIndex: "repositoryName",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "CWE",
        dataIndex: "cwesStrings",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, isMultipleValueSupported: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "CVE",
        dataIndex: "cve",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Base Score",
        dataIndex: "baseScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score" }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    },
    {
        title: "Overall Score",
        dataIndex: "cvssOverallScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score" }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    },
    {
        title: "Impact Score",
        dataIndex: "impactScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score" }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    }, {
        title: "Exploitability Score",
        dataIndex: "exploitabilityScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score" }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    }, ...(featureFlags.AATI_SCORE_COLUMN ? [{
        title: "AATI Score",
        dataIndex: "aatiScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score" }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    }]: []), {
        title: `${getVocabulary("findingScore")}`,
        dataIndex: "findingScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score" }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    }, ...(featureFlags.RISK_SCORE_3_0 ? [{
        title: `${getVocabulary("riskScore")}`,
        dataIndex: "riskScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score" }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    }, {
        title: `Risk Rating`,
        dataIndex: "riskRating",  
        ...getFilterProps({ filters: RISK_RATING_FILTERS, filterLabelFormatter: (name) => getVocabulary(name), sortOptions: Sorters.RISK_RATING('text',true)})
    }] : []), {
        title: "CVE Last Modified Date",
        dataIndex: "lastModifiedDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: "Finding Age",
        dataIndex: "foundOnDate",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON }),
        allFilterLabelFormatter: daysFieldAllLableFilterFormatter
    }, {
        title: "Found On",
        dataIndex: "foundOn",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    },
    {
        title: `${getVocabulary("triage")} Due Date`,
        dataIndex: "triageDueDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, 
    {
        title: `${getVocabulary("remediation")} Due Date`,
        dataIndex: "remediationDueDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, 
    {
        title: `${getVocabulary("resolution")} SLA Status`,
        dataIndex: "resolutionSlaStatus",
        ...getFilterProps({ filters: SLA_STATUS_FILTERS, nullFilterValue: null})
    },
    {
        title: `${getVocabulary("triage")} SLA Status`,
        dataIndex: "triageSlaStatus",
        ...getFilterProps({ filters: SLA_STATUS_FILTERS })
    },
    {
        title: `${getVocabulary("remediation")} SLA Status`,
        dataIndex: "remediationSlaStatus",
        ...getFilterProps({ filters: SLA_STATUS_FILTERS, nullFilterValue: null })
    },
    {
        title: `${getVocabulary("resolution")} Due Date`,
        dataIndex: "slaDueDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: `Last ${getVocabulary("confirmed")} Date`,
        dataIndex: "lastConfirmDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: `Last ${getVocabulary("falsePositive")} Date`,
        dataIndex: "lastFalsePositiveDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: `Last ${getVocabulary("open")} Date`,
        dataIndex: "lastOpenDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: `Last ${getVocabulary("acceptRisk")} Date`,
        dataIndex: "lastAcceptRiskDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: `Last ${getVocabulary("suppressed")} Date`,
        dataIndex: 'lastSuppressDate',
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: "Scan ID",
        dataIndex: "scan",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    },{
        title: `${getVocabulary("mitigated")} By Scan ID`,
        dataIndex: "mitigatedByScanId",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Last Changed",
        dataIndex: "lastUpdated",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: "Compliance",
        dataIndex: "policy",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='policy' />
        ),
    }, {
        title: `${getVocabulary("owner")}`,
        dataIndex: "owner",
        filters: filterValues.owner,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown 
                {...props} 
                skipSearchAPICall 
                name='owner' 
                componentProps={{ nullFilterValue: null }}
            />
        ),
    }, {
        title: `${getVocabulary("businessOwner")}`,
        dataIndex: "businessOwner",
        filters: filterValues.businessOwner,
       filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='businessOwner' />
        ),
    }, {
        title: `${getVocabulary("securityOwner")}`,
        dataIndex: "securityOwner",
        filters: filterValues.securityOwner,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='securityOwner' />
        ),
    }, {
        title: `${getVocabulary("engineeringOwner")}`,
        dataIndex: "engineeringOwner",
        filters: filterValues.engineeringOwner,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='engineeringOwner' />
        ),
    }, {
        title: `${getVocabulary("supportOwner")}`,
        dataIndex: "supportOwner",
        filters: filterValues.supportOwner,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='supportOwner' />
        ),
    }, {
        title: `${getVocabulary("complianceOwner")}`,
        dataIndex: "complianceOwner",
        filters: filterValues.complianceOwner,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='complianceOwner' />
        ),
    }, {
        title: "Developer",
        dataIndex: "developer",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='developer' />
        ),
    }, {
        title: "File Name",
        dataIndex: "filePath",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Tool URL",
        dataIndex: "url",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Component Name",
        dataIndex: "componentName",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='componentName' componentProps={{ nullFilterValue: null }} />
        ),
    }, {
        title: "Component Affected Version",
        dataIndex: "componentVersion",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='componentVersion' componentProps={{ nullFilterValue: null }} />
        ),
    }, {
        title: "Component Fix Version",
        dataIndex: "componentFixVersions",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='componentFixVersions' componentProps={{ nullFilterValue: null }} />
        ),
    }, {
        title: "Exploit Maturity",
        dataIndex: "SNYK_exploitMaturity",
        ...getFilterProps({ filters: [...EXPLOIT_MATURITY_FILTERS], nullFilterValue: null, filterLabelFormatter: value => _.isNull(value) || value === "N/A" ? CellRenderers.VALUE_OR_NA() : _.startCase(_.toLower(value)) })
    }, {
        title: "Fixable Using Tool",
        dataIndex: "fixability",
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='fixability' componentProps={{ nullFilterValue: null }} />
        ),
    }, {
        title: "Device",
        dataIndex: "vmHostName",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Image Name",
        dataIndex: "containerImageName",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Image Tag",
        dataIndex: "containerImageTag",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Twistlock Source",
        dataIndex: "Twistlock_scanType",
        ...getFilterProps({ filters: [...TWISTLOCK_SOURCE], nullFilterValue: null })
    }, {
        title: "Twistlock Origin Type",
        dataIndex: "Twistlock_originType",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "URL",
        dataIndex: "newUrl",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "Vendor",
        dataIndex: "vendor",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "CVE Published Date",
        dataIndex: "publishedDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: `Last ${getVocabulary("mitigated")} Date`,
        dataIndex: "issueResolved",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    }, {
        title: "Exploit Available",
        dataIndex: "exploited",
        filterLabelFormatter: renderBooleanValues,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='exploited' skipSearchAPICall componentProps={{sortOptions: Sorters.STRING("text", true), nullFilterValue: null}} />
        ),
    },{
        title: "XRay Impacted Artifact",
        dataIndex: "XRay_Impacted_Artifact",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    },{
        title: "XRay Path",
        dataIndex: "XRay_Path",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, {
        title: "IP Addresses",
        dataIndex: "vmHostIpAddresses",
        filterDropdown: (props) => <ColumnFilterSearchBoxWithExclude message={IPAdressFilterMessage}
            inputProps={{
                rules: [{
                  required: true,
                  message: "IP Address is required"
                }]
            }} {...props} 
        />,
    },{
        title: "Propose Action",
        dataIndex: "proposeAction",
        ...getFilterProps({ filters: filterValues.proposeAction, nullFilterValue: proposeActionNullFilter ? "null" : undefined, 
            filterLabelFormatter: value => {
                return PROPOSE_FILTER_OPTION_VALUES.hasOwnProperty(value) ? PROPOSE_FILTER_OPTION_VALUES[value]() : CellRenderers.VALUE_OR_BLANK()
            }
        })
    },{
        title: "Has Ticket",
        dataIndex: "armorcodeTicketExists",
        ...getFilterProps({ filters: [{ name: 'Yes', id: true }, {name: "No", id: false}] })
    },{
        title: `${getVocabulary("product")} Tier`,
        dataIndex: "productTier",
        ...getFilterProps({ filters: featureFlags.RISK_TIERS ? globalSettings.tierBasedTags : TIER_OPTIONS.map(option => ({ name: option.label, id: option.value })), nullFilterValue: null, nullFilterLabel: featureFlags.RISK_SCORE_3_0 ? 'Custom' : CellRenderers.VALUE_OR_NA(), filterLabelFormatter: value => value !== null ? value : featureFlags.RISK_SCORE_3_0 ? 'Custom' : CellRenderers.VALUE_OR_NA() })
    },{
    },{
        title: `${getVocabulary("subProduct")} Tier`,
        dataIndex: "subProductTier",
        ...getFilterProps({ filters: featureFlags.RISK_TIERS ? globalSettings.tierBasedTags : TIER_OPTIONS.map(option => ({ name: option.label, id: option.value })), nullFilterValue: null, nullFilterLabel: featureFlags.RISK_SCORE_3_0 ? 'Custom' : CellRenderers.VALUE_OR_NA(), filterLabelFormatter: value => value !== null ? value : featureFlags.RISK_SCORE_3_0 ? 'Custom' : CellRenderers.VALUE_OR_NA() })
    }, {
        title: `Last ${getVocabulary("triage")} Date`,
        dataIndex: "lastTriagedDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    },  ...(featureFlags.RISK_SCORE_3_0 ? [{
        title: `${getVocabulary("assetScore")}`,
        dataIndex: "assetScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score" }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    }] : []),
    {
        title: "CVSS Vector",
        dataIndex: "cvssVector",
        ...getFilterProps({ onlySearch: true, nullFilterValue: null, isMultipleValueSupported: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    }, 
    {
        title: "EPSS Score",
        dataIndex: "epssProbability",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score",
            applyInterceptor: (scores) => {
                return scores.map((score) => score ? (score / 100).toString() : score);
            },
            receiveInterceptor: (scores) => EPSSReceiveInterceptor(scores),
        }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    }, 
    {
        title: "EPSS Percentile",
        dataIndex: "epssScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score",
            applyInterceptor: (scores) => {
                return scores.map((score) => score ? (score / 100).toString() : score);
            },
            receiveInterceptor: (scores) => EPSSReceiveInterceptor(scores),
        }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    }, 
    {
        title: "CISA KEV",
        dataIndex: "isCISAKEV",
        filterLabelFormatter: renderBooleanValues,
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='isCISAKEV' skipSearchAPICall componentProps={{sortOptions: Sorters.STRING("text", true)}} />
        ),
    }, 
    {
        title: "Mobb Fix Available",
        dataIndex: "mobbFixUrl",
        ...getFilterProps({filters: [{ name: 'True', id: "true" }, {name: "False", id: "false"}]  })
    },
    {
        title: 'CISA Due Date',
        dataIndex: "cisaDueDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    },
    {
        title: "CTI Score",
        dataIndex: "ctiScore",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Score", nullFilterValue: null }),
        allFilterLabelFormatter: scoreFieldAllLableFilterFormatter,
    },
    {
        title: "Last Seen Date",
        dataIndex: "lastSeenDate",
        filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
        filterLabelFormatter: parseDateToDisplay,
    },
    {
        title: `${getVocabulary("group")}`,
        dataIndex: "groups",
        filterDropdown: (props) => <ColumnFilterGroups {...props} newVersion={true} />,
        filterLabelFormatter: ColumnFilterGroups.filterLabelFormatter,
    }, 
    ...(featureFlags.VERSIONS ? [{
        title: "Versioned Findings Count",
        dataIndex: "versionFindingsCount",
        filterType: ColumnFilterTypes.COMPARISON,
        ...getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "Count" }),
        allFilterLabelFormatter: countFieldAllLableFilterFormatter
    }] : []),
    {
        title: 'Correlation ID',
        dataIndex: "correlationIds",
        ...getFilterProps({ onlySearch: true, isMultipleValueSupported: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    },
    {
        title: 'Status Reason',
        dataIndex: 'statusChangeReason',
        ...getFilterProps({ filters: filterValues.statusChangeReason, nullFilterValue: null})
    },
    {
        title: 'Ticket Assignee',
        dataIndex: 'ticketOwnerNames',
        filterDropdown: (props) => (
            <ACContextualFilterDropdown {...props} name='ticketOwnerNames' componentProps={{ nullFilterValue: null }} />
        ),
    },
    {
        title: "Is Correlated",
        dataIndex: "correlatedFindingExits",
        ...getFilterProps({ filters: [{ name: 'True', id: true }, {name: "False", id: false}] })
    },
    {
        title: "Is Unique",
        dataIndex: "uniqueCorrelatedFindingExits",
        ...getFilterProps({ filters: [{ name: 'True', id: true }, {name: "False", id: false}] })
    },
    {
        title: "Remediation Group",
        dataIndex: "correlationName",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    },
    {
        title: "Remediation Action",
        dataIndex: "remediationAction",
        ...getFilterProps({ onlySearch: true, supportFilterOperation: featureFlags.NEW_TABLE_FILTER })
    },
    ...(featureFlags.OWASP_CWE_FILTER ? [{
        title: 'OWASP Top 10 - 2021',
        dataIndex: "owaspTop10_2021",
        ...getFilterProps({ filters: filterValues.owaspTop10_2021})
    }, {
        title: 'CWE Top 25 - 2024',
        dataIndex: "cweTop25_2024",
        ...getFilterProps({ filters: filterValues.cweTop25_2024})
    }] : []),
    ...(featureFlags.CRA ? [
        {
            title: 'Exploit Status',
            dataIndex: "craExploitStatus",
            ...getFilterProps({ filters: EXPLOIT_STATUS_OPTIONS.map((exp) => exp.label), nullFilterValue: null })
        },
        {
            title: 'CRA Notification Status',
            dataIndex: "craNotificationStatus",
            ...getFilterProps({ filters: NOTIFICATION_STATUS_OPTIONS.map((exp) => exp.label), nullFilterValue: null })
        },
        {
            title: 'CRA Early Warning Due',
            dataIndex: "craEarlyWarningDue",
            filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
            filterLabelFormatter: parseDateToDisplay,
        },
        {
            title: 'CRA Notification Due',
            dataIndex: "craNotificationDue",
            filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
            filterLabelFormatter: parseDateToDisplay,
        },
        {
            title: "CRA Final Report Due",
            dataIndex: "craFinalReportDue",
            filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
            filterLabelFormatter: parseDateToDisplay,
        },
        {
            title: "CRA User Disclosure Due",
            dataIndex: "craUserDisclosureDue",
            filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
            filterLabelFormatter: parseDateToDisplay,
        },
        {
            title: "CRA User Disclosure Status",
            dataIndex: "craUserDisclosureStatus",
            ...getFilterProps({ filters: USER_DISCLOSURE_STATUS_OPTIONS.map((exp) => exp.label), nullFilterValue: null })
        },
    ] : [])
    ];

    if(featureFlags.RBVM_ASSETS){
        columns.push({
            title: "Asset Details",
            dataIndex: "assetDetails",
            items: [
                { label: "Name", value: "assetName" },
                { label: "ID", value: "assetId" },
                { label: "Type", value: "assetType" },
                { label: "OS", value: "assetOS" },
                { label: "OS Versions", value: "assetOSVersions" },
                { label: "Cloud Provider", value: "assetCloudProviders" },
                { label: "Location", value: "assetLocations" },
                { label: "Status", value: "assetActive" },
                // { label: "Date", value: "assetDate" },
                // { label: "Tags", value: "assetTag" },
                { label: "IPv4", value: "assetIPV4" },
                { label: "IPv6", value: "assetIPV6" },
                {label: "Asset Owner", value: "assetOwner"}
            ].sort(Sorters.STRING("label")),
            filterOptionMap: {
            ...(pick(filterValues, [
                "assetType",
                "assetOS",
                "assetOSVersions",
                "assetCloudProviders",
                "assetLocations",
                "assetOwner"
            ])),
            assetOwner: [{ name: 'N/A', id: null }, ...(filterValues.assetOwner || [])],
            assetActive: [{ name: 'Active', id: "true" }, {name: "Inactive", id: "false"}]
        },
            filterTypeMap: {
                assetType: "multiselect",
                assetActive: "multiselect",
                assetOS: "multiselect",
                assetOSVersions : "multiselect",
                assetCloudProviders: "multiselect",
                assetLocations: "multiselect",
                assetDate: "daterange",
                assetTag: "tag",
                assetIPV4: "ip",
                assetIPV6: "ip",
                assetOwner: "multiselect"
            },
            filterDropdown: (props) => <ACColumnFilterGroup {...props} />,
        });
    }

    // append "keyValue." to dataIndex and key to avoid clasing with OOB columns
    _.forEach(externalColumn, col => {
        columns.push({
            title: col.columnName,
            dataIndex: `keyValue.${col.key}`,
            key: `keyValue.${col.key}`,
            filterDropdown: (props) => (
                <ACCustomAttributeFilterDropdown 
                    scope={CustomColumnScope.FINDING} 
                    useTagsAPIV2
                    attribute={col} 
                    componentProps={props} 
                    contextual
                />
            ),
        });
    })

    _.forEach(findingCustomFields, col => {
        const filters = col.type === 'boolean' ? [{ name: 'True', id: true }, {name: "False", id: false}] : [];
        const isNumericType = col.type === 'float' || col.type === 'long';
        columns.push({
            title: col.label,
            dataIndex: col.id,
            key: col.id,
            ...(col.type === 'date' ? {
                filterDropdown: (props) => <ACDateRangeColumnFilter {...props} />,
                filterLabelFormatter: parseDateToDisplay,
            } : col.type === 'boolean' ? getFilterProps({ filters }) : isNumericType ? getFilterProps({ filterType: ColumnFilterTypes.COMPARISON, filterCompareLabel: "" }) : getFilterProps({ filterType: ColumnFilterTypes.SEARCH }))
        });
    })

    return columns.filter(c => !isEmpty(c)).map(c => ({...c, key: c.key || c.dataIndex, filterLabelFormatter: c.filterLabelFormatter || CellRenderers.VALUE_OR_NA}));
}

export const StatusCountSection = forwardRef(function StatusCountSection(props, ref){
    const [expanded, setExpanded] = useState(false);
    const [stats, setStats] = useState();
    const filters = useUrlFilters();
    const actualAppliedFilters = useDetectFilterChange(filters);
    const { businessUnitId } = useActiveUserContext();
    const { globalFilterHash } = useGlobalFilterContext();
    const refresh = useTrigerrableEffect(() => {
        setStats();
        const { promise, cancel } = FindingAPI.getStatsAll({ filters: actualAppliedFilters }, undefined, false);
        promise.then(({stats}) => setStats(stats)).catch(()=>setStats(null));
        return cancel;
    }, [actualAppliedFilters, globalFilterHash]);

    useImperativeHandle(ref, () => ({ refresh }), [refresh]);

    const severities = useActiveSeveritiesKeys(undefined, true);

    const toggleExpanded = useCallback(() => setExpanded(prev => !prev), []);

    const renderStatus = (name, stat) => {
        const loading = _.isUndefined(stats);
        return (
            <div className={classNames("m-r-xs border-right", { "p-r-xs": !expanded })}>
                <div>
                <Vocabulary name={name} /> <Text strong><Tooltip title={`${formatNumberNew(stat?.count ?? 0, 'long')}`}>{loading ? <LoadingIcon /> : formatNumberNew(stat?.count ?? 0, 'short')}</Tooltip></Text>
                </div>
                <div className='d-flex flex-wrap status-count-severity' style={{ height: expanded ? Math.ceil(severities.length / 2) * 28 : 0 }}>
                    {
                        _.map(severities, severity => (
                            <div key={severity} style={{ borderLeft: `2px solid ${SEVERITY_COLOR[severity]}`, width: "50%" }} className="p-x-xs m-t-xs">
                                <Text><Tooltip title={`${getVocabulary(severity)} ${formatNumberNew(stat?.severity[severity] ?? 0, 'long')}`}>{loading ? <LoadingIcon /> : (formatNumberNew(stat?.severity[severity] ?? 0, 'short'))}</Tooltip></Text>
                            </div>
                        ))
                    }
                </div>
            </div>
        )
    }

    return (
        <div className='bg-white p-x-xs status-count-section' onClick={toggleExpanded}>
            <div className={classNames('p-x-xs p-y-sm border-radius status-count-container flex-1 d-flex justify-content-between', { expanded })}>
                <div className='d-flex'>
                    {renderStatus("open", stats?.[FINDING_STATUS.OPEN])}
                    {renderStatus("triageStatus", stats?.[FINDING_STATUS.TRIAGE])}
                    {renderStatus("confirmed", stats?.[FINDING_STATUS.CONFIRMED])}
                    {renderStatus("acceptRisk", stats?.[FINDING_STATUS.ACCEPT_RISK])}
                    {renderStatus("falsePositive", stats?.[FINDING_STATUS.FALSE_POSITIVE])}
                    {renderStatus("mitigated", stats?.[FINDING_STATUS.MITIGATED])}
                    {renderStatus("suppressed", stats?.[FINDING_STATUS.SUPPRESSED])}
                    {renderStatus("Total", stats?.Reported)}
                </div>
                <div>
                    <div className='d-flex align-items-center'>
                        <ProductSubProductCount businessUnitId={businessUnitId}/> {expanded ? <AngleUpIcon /> : <AngleDownIcon />}
                    </div>
                </div>
            </div>
        </div>
    )
});

export const disabledActionsForSelectAll = [
//   BULK_FINDING_ACTION_KEYS.CREATE_TICKETS,
  BULK_FINDING_ACTION_KEYS.PROPOSE_MITIGATED,
  BULK_FINDING_ACTION_KEYS.MITIGATED,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_ACCEPTRISK,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_ASSIGNEE,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_BASIC_CHANGE,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_CATEGORY,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_CONFIRMED,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_DUE_DATE,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_FALSE_POSITIVE,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_OPEN,
//   BULK_FINDING_ACTION_KEYS.PROPOSE_SEVERITY,
];

const NA = CellRenderers.VALUE_OR_NA();
export const renderBooleanValues = (value) =>  (_.isNil(value) || value === NA) ? NA : _.capitalize(value);